import { type BillCategory, BillCategoryNames } from '@fruitful/types';
import { useAtom } from 'jotai';
import { FlatList } from 'react-native';
import {
    Card,
    isWeb,
    PillButton,
    ScrollView,
    useWindowDimensions,
    View,
    XStack,
} from 'smoothie';

import { isEmpty } from '../../../common/misc';
import { convertCategoryToIcon } from '../../../common/utils/billUtils';
import { selectedBillCategoriesAtom } from '../../state/BillsAtoms';

const PaddingCard = () => {
    return <Card backgroundColor="rgba(0,0,0,0)" width={20}></Card>;
};

interface BillFilterPillProps {
    billCategories: BillCategory[];
}

const BillFilterPills = ({ billCategories }: BillFilterPillProps) => {
    const [selectedBillCategories, setSelectedBillCategories] = useAtom(
        selectedBillCategoriesAtom,
    );

    const { width } = useWindowDimensions();

    const renderPillButton = (billCategory: string, index: number) => {
        const displayName =
            billCategory === 'all'
                ? 'All'
                : (BillCategoryNames[billCategory] as string);
        return (
            <PillButton
                key={index}
                pillProps={{
                    backgroundColor: '$gray100',
                    borderWidth: 2,
                    borderColor: categoryIsSelected(billCategory)
                        ? '$black'
                        : '$gray100',
                }}
                text={displayName}
                icon={convertCategoryToIcon(billCategory)}
                onPress={() => {
                    if (selectedBillCategories.includes(billCategory)) {
                        setSelectedBillCategories([]);
                    } else {
                        if (billCategory === 'all') {
                            setSelectedBillCategories([]);
                        } else {
                            setSelectedBillCategories([billCategory]);
                        }
                    }
                }}
            />
        );
    };

    const categoryIsSelected = (category: string) => {
        if (isEmpty(selectedBillCategories) && category === 'all') {
            return true;
        }
        return selectedBillCategories.includes(category);
    };

    return isWeb ? (
        <FlatList
            style={{ maxWidth: width }}
            data={['all', ...billCategories]}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
                overflow: 'visible',
            }}
            renderItem={({ item, index }) => (
                <View
                    paddingLeft={index === 0 ? 20 : 0}
                    paddingRight={index === billCategories.length ? 20 : 0}
                >
                    {renderPillButton(item, index)}
                </View>
            )}
            ItemSeparatorComponent={() => <View style={{ width: 10 }} />}
        />
    ) : (
        <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            marginHorizontal={-32}
        >
            <XStack gap={10} flexWrap="wrap">
                <PaddingCard />
                {renderPillButton('all', 0)}
                {billCategories.map((billCategory, index) =>
                    renderPillButton(billCategory, index + 1),
                )}
                <PaddingCard />
            </XStack>
        </ScrollView>
    );
};

export default BillFilterPills;
